import { apiClient } from '../utils/api';

export interface TransactionSummary {
  totalEarned: string;
  availableToWithdraw: string;
  totalRefunds: string;
  totalShippingFees: string;
  totalPlatformFees: string;
}

export interface ApiTransaction {
  id: string;
  date: string;
  description: string;
  amount: number;
  type: 'payment' | 'refund' | 'withdrawal' | 'fee';
  status: 'completed' | 'processing' | 'failed' | 'pending';
  paymentId?: string;
  visaId?: string;
  eventId?: string;
  eventName?: string;
  eventDate?: string;
}

export interface TransactionsPagination {
  currentPage: number;
  totalPages: number;
  totalItems: number;
  hasMore: boolean;
  limit: number;
}

export interface VendorLatestPayout {
  payoutRequestId: string;
  /** Net amount owed to the vendor (grossAmount - feeAmount), in QAR. */
  amount: number;
  grossAmount: number;
  feeAmount: number;
  orderCount: number;
  /**
   * `new` is still awaiting admin action. `paid` / `failed` are resolved and are
   * still returned so the portal keeps showing the payout until the next period
   * creates a newer one.
   */
  status: 'new' | 'paid' | 'failed';
  /** Epoch ms bounds of the payout period this request covers. */
  periodStart: number;
  periodEnd: number;
  createdAt: number;
  /** Epoch ms the admin marked the request paid, when it has been. */
  paidAt?: number;
  /** Why the admin marked the request failed, when it has been. */
  failureReason?: string;
}

export interface TransactionsApiResponse {
  success: boolean;
  summary: TransactionSummary;
  count: number;
  data: ApiTransaction[];
  pagination: TransactionsPagination;
  /** Newest payout request of any status, or null when the vendor never had one. */
  latestPayoutRequest: VendorLatestPayout | null;
}

export interface GetTransactionsParams {
  paymentId?: string;
  page?: number;
  limit?: number;
  search?: string;
}

// Get transactions (server-paginated and searchable via the `q` query param)
export const getTransactions = async (params?: GetTransactionsParams): Promise<TransactionsApiResponse> => {
  const query = new URLSearchParams();
  if (params?.paymentId) query.set('paymentId', params.paymentId);
  if (params?.page && params.page > 1) query.set('page', String(params.page));
  if (params?.limit) query.set('limit', String(params.limit));
  if (params?.search) query.set('q', params.search);
  const queryString = query.toString();
  const url = queryString ? `/vendors/transactions?${queryString}` : '/vendors/transactions';
  const response = await apiClient.get<TransactionsApiResponse>(url);
  return response.data;
};

// Get transactions with type filter (you can implement this when the API endpoint supports it)
export const getTransactionsByType = async (type: string): Promise<TransactionsApiResponse> => {
  const response = await apiClient.get<TransactionsApiResponse>(`/transactions?type=${type}`);
  return response.data;
};

// Request payout (you can implement this when the API endpoint is ready)
export const requestPayout = async (amount: number): Promise<any> => {
  const response = await apiClient.post('/transactions/payout', { amount });
  return response.data;
};

// Update withdrawal account (you can implement this when the API endpoint is ready)
export const updateWithdrawalAccount = async (accountDetails: {
  bankName?: string;
  bankAccountName?: string;
  bankAccountNumber?: string;
}): Promise<any> => {
  const response = await apiClient.post('/vendors/edit-profile', accountDetails);
  return response.data;
};
